'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { graphql } from '~/gql'
import {
  MembersListQuery,
  WorkspaceInviteInput,
  WorkspaceInviteInputs,
} from '~/gql/graphql'
import { useGQLQuery, useGQLSuspenseQuery } from '~/hooks/useGraphQL'
import { gqlClient } from '~/hooks/useGraphQL.server'

export const workspaceMembersQuery = graphql(`
  query MembersList($workspaceId: String!) {
    getWorkspaceMembers(workspaceId: $workspaceId) {
      memberId
      orgId
      user {
        memberId
        profile {
          email
          avatar
          fullName
        }
      }
      roles
      permissions
      inviteStatus
    }
  }
`)

// 10 Minutes
const staleTime = 10 * 60 * 1000

export const useWorkspaceMembersQuery = ({
  workspaceId,
  enabled,
}: {
  workspaceId: string
  enabled?: boolean
}) => {
  const queryClient = useQueryClient()
  
  // Memoize enabled state to prevent unnecessary query re-runs
  const isEnabled = useMemo(
    () => !!workspaceId && (typeof enabled === 'undefined' || enabled === true),
    [workspaceId, enabled]
  )
  
  // Memoize variables object to ensure stable query key
  // This prevents React Query from treating it as a new query on each render
  const variables = useMemo(
    () => ({ workspaceId }),
    [workspaceId]
  )
  
  // Check if query is already in cache or fetching
  // Match the query key format used by useGQLQuery: ['MembersList', variables ?? undefined]
  const queryKey = useMemo(
    () => ['MembersList', variables ?? undefined] as const,
    [variables]
  )
  
  const cachedData = queryClient.getQueryData(queryKey)
  const isFetching = queryClient.isFetching({ queryKey })
  
  // Move debug logging to useEffect to prevent logging on every render
  useEffect(() => {
    if (typeof window !== 'undefined' && (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'dev')) {
      console.log(`[useWorkspaceMembersQuery] workspaceId=${workspaceId}, enabled=${enabled}, isEnabled=${isEnabled}, hasCache=${!!cachedData}, isFetching=${isFetching}, queryKey=`, queryKey)
    }
  }, [workspaceId, enabled, isEnabled, cachedData, isFetching, queryKey])
  
  // Only enable if we don't have cached data and not already fetching
  // This prevents duplicate requests when multiple components render simultaneously
  const shouldEnable = isEnabled && !cachedData && !isFetching
  
  // Let React Query handle automatic deduplication
  // Multiple components calling this hook with the same workspaceId will
  // automatically share the same query and result
  return useGQLQuery(
    workspaceMembersQuery,
    variables,
    {
      enabled: shouldEnable,
      staleTime,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  )
}

export const useWorkspaceMembersSuspenseQuery = (workspaceId: string) => {
  const queryClient = useQueryClient()
  
  // Memoize variables to ensure stable query key
  const variables = useMemo(
    () => ({ workspaceId }),
    [workspaceId]
  )
  
  // Check if query is already in cache or fetching (same logic as regular query)
  // Match the query key format used by useGQLQuery: ['MembersList', variables ?? undefined]
  const queryKey = useMemo(
    () => ['MembersList', variables ?? undefined] as const,
    [variables]
  )
  
  const cachedData = queryClient.getQueryData(queryKey)
  const isFetching = queryClient.isFetching({ queryKey })
  
  // Only enable if we don't have cached data and not already fetching
  // This ensures suspense queries share the same cache and don't duplicate requests
  const shouldEnable = !!workspaceId && !cachedData && !isFetching
  
  return useGQLSuspenseQuery(
    workspaceMembersQuery,
    { enabled: shouldEnable },
    variables
  )
}

const workspaceInviteMutation = graphql(`
  mutation InviteMemberToWorkspace($input: WorkspaceInviteInput!) {
    inviteMemberToWorkspace(input: $input) {
      memberId
    }
  }
`)

export const useWorkspaceInviteMutation = () =>
  useMutation({
    mutationFn: async (input: WorkspaceInviteInput) =>
      gqlClient().request(workspaceInviteMutation, {
        input,
      }),
  })

const inviteMembersToWorkspaceMutation = graphql(`
  mutation InviteMembersToWorkspace($input: WorkspaceInviteInputs!) {
    inviteMembersToWorkspace(input: $input) {
      memberId
    }
  }
`)

export const useInviteMembersToWorkspaceMutation = (workspaceId: string) => {
  const queryClient = useQueryClient()
  const memberListQueryKey = useMemo(
    () => ['MembersList', { workspaceId }] as const,
    [workspaceId]
  )

  return useMutation({
    mutationFn: async (input: WorkspaceInviteInputs) =>
      gqlClient().request(inviteMembersToWorkspaceMutation, {
        input,
      }),
    async onSuccess(_data, variables) {
      await queryClient.cancelQueries({ queryKey: memberListQueryKey })

      const previousCollaborators =
        queryClient.getQueryData<MembersListQuery>(memberListQueryKey)

      const previousCollaboratorEmails = (
        previousCollaborators?.getWorkspaceMembers ?? []
      ).map((member) => member.user?.profile?.email)

      // Only need to add placeholder invites which do not currently exist in the list
      const newInvites = variables.invites.filter(
        (invitee) =>
          !previousCollaboratorEmails.includes(invitee.inviteeEmail ?? '')
      )

      queryClient.setQueryData<MembersListQuery>(memberListQueryKey, (old) => ({
        __typename: 'Query',
        ...(old ?? {}),
        getWorkspaceMembers: [
          ...(old?.getWorkspaceMembers ?? []),
          ...newInvites.map((invitee) => ({
            roles: invitee.roles ?? 'viewer',
            memberId: invitee.inviteeEmail ?? '',
            user: {
              memberId: invitee.inviteeEmail ?? '',
              profile: {
                avatar: '',
                email: invitee.inviteeEmail ?? '',
                fullName: invitee.inviteeEmail ?? '',
              },
            },
            inviteStatus: 'INVITED',
          })),
        ],
      }))
    },
  })
}
